import { ChangeEvent, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { DateTime } from 'luxon';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTimePicker } from "@mui/x-date-pickers";
import { Stack, Grid, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, styled } from '@mui/material';
import { BlankMenuItem } from '../../util/SharedStyles';
import CloseIcon from '@mui/icons-material/Close';
import { defaultDateFromFilterDate, Viewer } from "../../util/Constants";
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from '../../util/ViewerUtility';
import { capitalizeWithUnderscoreRemoval, PremadeErrorLogFilterSet } from '../../util/Common';
import { fetchClientNameSearchResults } from '../../features/Clients/ClientsActions';
import { fetchClientNameSearchResultsSuccess, selectClientNameSearchResults } from '../../features/Clients/ClientsSlice';
import { fetchTradingPartnerNameSearchResults } from '../../features/TradingPartners/TradingPartnersActions';
import { fetchTradingPartnerNameSearchResultsSuccess, selectTradingPartnerNameSearchResults } from '../../features/TradingPartners/TradingPartnersSlice';
import { ExceptionStatus, ProcessName, TransactionErrorCategoryModel } from '../../gql-types.generated';
import { AutocompleteSearchResultModel } from '../../util/Common';
import FilterBar from "./FilterBar";
import AutocompleteFilterField from '../fields/AutocompleteFilterField';
import { selectTransactionErrorCategoryList } from '../../features/EDIContainer/EDIContainerSlice';

const DatePickerWrapper = styled('div')((props) => ({
    width: '100%',
}));

interface LogsFilterBarProps {
    id?: string;
    loading: boolean;
    visible: boolean;
    viewer: Viewer | undefined;
    premadeSet?: PremadeErrorLogFilterSet | null;
    restoreFromActive?: boolean;
    onClose: () => void;
    onFilterChanged: (
        filterClient: string | undefined, 
        filterClientId?: string | undefined,
        filterIO?: string | undefined, 
        filterDocType?: string | undefined,
        filterPartner?: string | undefined,
        filterPartnerId?: string | undefined,
        filterPONum?: string | undefined,
        filterDocNum?: string | undefined,
        filterStatusVal?: ExceptionStatus | undefined,
        filterProcess?: ProcessName | undefined,
        filterDocDateFrom?: DateTime | undefined,
        filterDocDateTo?: DateTime | undefined,
        filterOriginalFileName?: string | undefined,
        filterCategory?: string | undefined
    ) => void;
    onSaveActiveFilters?: (saveFunc: () => void) => void;
}

const ErrorLogsFilterBar: React.FC<LogsFilterBarProps> = props => {
    const { id, loading = false, visible = false, viewer, premadeSet, restoreFromActive = false, onFilterChanged, onSaveActiveFilters } = props;
    const dispatch = useAppDispatch();
    const [filterClientName, setFilterClientName] = useState<string | undefined>();
    const [filterClientId, setFilterClientId] = useState<string | undefined>();
    const [searchClientName, setSearchClientName] = useState<string>('');
    const [filterDirection, setFilterDirection] = useState<string | undefined>();
    const [filterDocType, setFilterDocType] = useState<string | undefined>();
    const [filterPartnerName, setFilterPartnerName] = useState<string | undefined>(undefined);
    const [filterPartnerId, setFilterPartnerId] = useState<string | undefined>(undefined);
    const [searchPartnerName, setSearchPartnerName] = useState<string>('');
    const [filterPO, setFilterPO] = useState<string | undefined>(undefined);
    const [filterDocNumber, setFilterDocNumber] = useState<string | undefined>(undefined);
    const [filterProcessName, setFilterProcessName] = useState<ProcessName | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<ExceptionStatus | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const [filterFileName, setFilterFileName] = useState<string | undefined>(undefined);
    const [filterCategoryId, setFilterCategoryId] = useState<string | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);
    const [searchDisabled, setSearchDisabled] = useState(false);
    const fieldsDisabled = visible && loading;

    const clientNameSearchResults = useAppSelector(selectClientNameSearchResults);
    const partnerNameSearchResults = useAppSelector(selectTradingPartnerNameSearchResults);

    const categories = useAppSelector(selectTransactionErrorCategoryList);
    const statuses = Object.values(ExceptionStatus);
    const processes = Object.values(ProcessName);
    
    const onSearchClick = () => {
        onFilterChanged(filterClientName, filterClientId, filterDirection, filterDocType, filterPartnerName, filterPartnerId, filterPO, filterDocNumber, filterStatus, filterProcessName, filterDateFrom, filterDateTo, filterFileName, filterCategoryId);
    }

    const onCloseClick = () => {
        clearAndRemoveLocalFilters();
        props.onClose();
    };

    useEffect(() => {
        if (filterCount > 0) {
            setSearchDisabled(false);
        } else {
            setSearchDisabled(true);
        }
    }, [filterCount]);

    useEffect(() => {
        let numFilters = 0;

        if (filterClientName && filterClientName.length > 0) {
            numFilters += 1;
        }
        if (filterDirection && filterDirection.length > 0) {
            numFilters += 1;
        }
        if (filterDocType && filterDocType.length > 0) {
            numFilters += 1;
        }
        if (filterPartnerName && filterPartnerName.length > 0) {
            numFilters += 1;
        }
        if (filterPO && filterPO.length > 0) {
            numFilters += 1;
        }
        if (filterDocNumber && filterDocNumber.length > 0) {
            numFilters += 1;
        }
        if (filterStatus && filterStatus.length > 0) {
            numFilters += 1;
        }
        if (filterProcessName && filterProcessName.length > 0) {
            numFilters += 1;
        }
        if (filterFileName && filterFileName.length > 0) {
            numFilters += 1;
        }
        if (filterCategoryId && filterCategoryId.length > 0) {
            numFilters += 1;
        }
        if (filterDateFrom) {
            numFilters += 1;
        }
        if (filterDateTo) {
            numFilters += 1;
        }

        setFilterCount(numFilters);
    }, [filterClientName, filterDirection, filterDocType, filterPartnerName, filterPO, filterDocNumber, filterStatus, filterProcessName, filterDateFrom, filterDateTo, filterFileName, filterCategoryId]);

    useEffect(() => {
        // Expose the save function to parent component
        if (onSaveActiveFilters) {
            onSaveActiveFilters(saveActiveFilters);
        }
    }, [onSaveActiveFilters, filterClientName, filterClientId, filterDirection, filterDocType, filterPartnerName, filterPartnerId, filterPO, filterDocNumber, filterStatus, filterProcessName, filterDateFrom, filterDateTo, filterFileName, filterCategoryId]);

    useEffect(() => {
        // Priority order:
        // 1. Restore from active filters (returning from details)
        // 2. Apply premade filters (coming from dashboard)
        // 3. Load from regular localStorage (normal navigation)
        if (restoreFromActive) {
            loadFromActiveFilters();
            return; // Active filters were restored, exit
        }
        
        // premadeSet has 3 conditions
        // - null - the possible ErrorLog Param hasn't been set, Do nothing so multiple calls are not made
        // - undefined - there was no ErrorLog Param, Check for LocalStorage filters
        // - PremadeErrorLogFilterSet Object - premade filters to be applied
        if (premadeSet !== null) {
            if (premadeSet) {
                setFromPremade();
            } else {
                setFromLocalStorage();
            }
        }
    }, [premadeSet, restoreFromActive])

    
    const saveActiveFilters = () => {
        // Save current filter state to "active" localStorage keys
        if (filterClientName) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterClientName', filterClientName);
        }
        if (filterClientId) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterClientId', filterClientId);
        }
        if (filterDirection) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterDirection', filterDirection);
        }
        if (filterDocType) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterDocType', filterDocType);
        }
        if (filterPartnerName) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterPartnerName', filterPartnerName);
        }
        if (filterPartnerId) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterPartnerId', filterPartnerId);
        }
        if (filterPO) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterPO', filterPO);
        }
        if (filterDocNumber) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterDocNumber', filterDocNumber);
        }
        if (filterStatus) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterStatus', filterStatus);
        }
        if (filterProcessName) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterProcessName', filterProcessName);
        }
        if (filterDateFrom) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterDateFrom', filterDateFrom.toISO());
        }
        if (filterDateTo) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterDateTo', filterDateTo.toISO());
        }
        if (filterFileName) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterFileName', filterFileName);
        }
        if (filterCategoryId) {
            setLocalStorageItem(viewer, 'errorLogActiveFilterCategory', filterCategoryId);
        }
    };

    const loadFromActiveFilters = () => {
        clearFilters();
        
        // Load all active filters
        let name = getLocalStorageItem(viewer, "errorLogActiveFilterClientName");
        if (name != null) {
            setFilterClientName(name);
        }
        let clientId = getLocalStorageItem(viewer, "errorLogActiveFilterClientId");
        if (clientId != null) {
            setFilterClientId(clientId);
        }
        let type = getLocalStorageItem(viewer, "errorLogActiveFilterDocType");
        if (type != null) {
            setFilterDocType(type);
        }
        let po = getLocalStorageItem(viewer, "errorLogActiveFilterPO");
        if (po != null) {
            setFilterPO(po);
        }
        let docNum = getLocalStorageItem(viewer, "errorLogActiveFilterDocNumber");
        if (docNum != null) {
            setFilterDocNumber(docNum);
        }
        let partner = getLocalStorageItem(viewer, "errorLogActiveFilterPartnerName");
        if (partner != null) {
            setFilterPartnerName(partner);
        }
        let partnerId = getLocalStorageItem(viewer, "errorLogActiveFilterPartnerId");
        if (partnerId != null) {
            setFilterPartnerId(partnerId);
        }
        let direction = getLocalStorageItem(viewer, "errorLogActiveFilterDirection");
        if (direction != null) {
            setFilterDirection(direction);
        }
        let process = getLocalStorageItem(viewer, "errorLogActiveFilterProcessName");
        let processName = undefined;
        if (process != null) {
            processName = process as ProcessName;
            setFilterProcessName(processName);
        }
        let status = getLocalStorageItem(viewer, "errorLogActiveFilterStatus");
        let docStatus = undefined;
        if (status != null) {
            docStatus = status as ExceptionStatus;
            setFilterStatus(docStatus);
        }
        let dateFrom = getLocalStorageItem(viewer, "errorLogActiveFilterDateFrom");
        let dateFromAsDateTime = undefined;
        if (dateFrom != null) {
            dateFromAsDateTime = DateTime.fromISO(dateFrom);
            setFilterDateFrom(dateFromAsDateTime);
        }
        let dateTo = getLocalStorageItem(viewer, "errorLogActiveFilterDateTo");
        let dateToAsDateTime = undefined;
        if (dateTo != null) {
            dateToAsDateTime = DateTime.fromISO(dateTo);
            setFilterDateTo(dateToAsDateTime);
        }
        let fileName = getLocalStorageItem(viewer, "errorLogActiveFilterFileName");
        if (fileName != null) {
            setFilterFileName(fileName);
        }
        let category = getLocalStorageItem(viewer, "errorLogActiveFilterCategory");
        if (category != null) {
            setFilterCategoryId(category);
        }

        // Trigger search with the restored active filters
        onFilterChanged(
            name ?? undefined, 
            clientId ?? undefined,
            direction ?? undefined, 
            type ?? undefined, 
            partner ?? undefined, 
            partnerId ?? undefined,
            po ?? undefined, 
            docNum ?? undefined, 
            docStatus, 
            processName, 
            dateFromAsDateTime, 
            dateToAsDateTime,
            fileName ?? undefined,
            category ?? undefined
        );

        // Clear active filters after loading
        clearActiveFilters();
    };

    const clearActiveFilters = () => {
        removeLocalStorageItem(viewer, 'errorLogActiveFilterClientName');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterClientId');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterDirection');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterDocType');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterPartnerName');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterPartnerId');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterPO');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterDocNumber');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterStatus');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterProcessName');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterDateFrom');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterDateTo');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterFileName');
        removeLocalStorageItem(viewer, 'errorLogActiveFilterCategory');
    };

    const setFromLocalStorage = () => {
        clearFilters();
        let haveLocalStoredFilters = false;
    
        // get the filters in localStorage if there are any 
        let name = getLocalStorageItem(viewer, "errorLogFilterClientName");
        if (name != null) {
            setFilterClientName(name);
            haveLocalStoredFilters = true;
        }
        let clientId = getLocalStorageItem(viewer, "errorLogFilterClientId");
        if (clientId != null) {
            setFilterClientId(clientId);
            haveLocalStoredFilters = true;
        }
        let type = getLocalStorageItem(viewer, "errorLogFilterDocType");
        if (type != null) {
            setFilterDocType(type);
            haveLocalStoredFilters = true;
        }
        let po = getLocalStorageItem(viewer, "errorLogFilterPONum");
        if (po != null) {
            setFilterPO(po);
            haveLocalStoredFilters = true;
        }
        let docNum = getLocalStorageItem(viewer, "errorLogFilterDocNum");
        if (docNum != null) {
            setFilterDocNumber(docNum);
            haveLocalStoredFilters = true;
        }
        let partner = getLocalStorageItem(viewer, "errorLogFilterPartnerName");
        if (partner != null) {
            setFilterPartnerName(partner);
            haveLocalStoredFilters = true;
        }
        let partnerId = getLocalStorageItem(viewer, "errorLogFilterPartnerId");
        if (partnerId != null) {
            setFilterPartnerId(partnerId);
            haveLocalStoredFilters = true;
        }
        let direction = getLocalStorageItem(viewer, "errorLogFilterDirection");
        if (direction != null) {
            setFilterDirection(direction);
            haveLocalStoredFilters = true;
        }
        let process = getLocalStorageItem(viewer, "errorLogFilterProcessName");
        let processName = undefined;
        if (process != null) {
            processName = process as ProcessName;
            setFilterProcessName(processName);
            haveLocalStoredFilters = true;
        }
        let status = getLocalStorageItem(viewer, "errorLogFilterStatus");
        let docStatus = undefined;
        if (status != null) {
            docStatus = status as ExceptionStatus;
            setFilterStatus(docStatus);
            haveLocalStoredFilters = true;
        }
        let dateFrom = getLocalStorageItem(viewer, "errorLogFilterDateFrom");
        let dateFromAsDateTime = undefined;
        if (dateFrom != null) {
            dateFromAsDateTime = DateTime.fromISO(dateFrom);
            setFilterDateFrom(dateFromAsDateTime);
            haveLocalStoredFilters = true;
        }
        let dateTo = getLocalStorageItem(viewer, "errorLogFilterDateTo");
        let dateToAsDateTime = undefined;
        if (dateTo != null) {
            dateToAsDateTime = DateTime.fromISO(dateTo);
            setFilterDateTo(dateToAsDateTime);
            haveLocalStoredFilters = true;
        }
        let fileName = getLocalStorageItem(viewer, "errorLogFilterFileName");
        if (fileName != null) {
            setFilterFileName(fileName);
            haveLocalStoredFilters = true;
        }
        let category = getLocalStorageItem(viewer, "errorLogFilterCategory");
        if (category != null) {
            setFilterCategoryId(category);
            haveLocalStoredFilters = true;
        }

        // If no filters were found in localStorage, apply default date filter (7 days ago)
        if (!haveLocalStoredFilters) {
            dateFromAsDateTime = defaultDateFromFilterDate;
            setFilterDateFrom(dateFromAsDateTime);
        }

        // onSearchClick relies on the all the states to set in time,
        // set them above for filterbar visuals
        // but make the call directly with the values (localStorage or defaults)
        onFilterChanged(
            name ?? undefined, 
            clientId ?? undefined,
            direction ?? undefined, 
            type ?? undefined, 
            partner ?? undefined, 
            partnerId ?? undefined,
            po ?? undefined, 
            docNum ?? undefined, 
            docStatus, 
            processName, 
            dateFromAsDateTime, 
            dateToAsDateTime,
            fileName ?? undefined,
            category ?? undefined
        );
    };

    const setFromPremade = () => {
        if (!premadeSet) return;
        clearFilters();
        if (premadeSet.filterClient) {
            setFilterClientName(premadeSet.filterClient);
        }
        if (premadeSet.filterDocDateFrom) {
            setFilterDateFrom(premadeSet.filterDocDateFrom);
        }
        if (premadeSet.filterDocDateTo) {
            setFilterDateTo(premadeSet.filterDocDateTo);
        }
        if (premadeSet.filterDocNum) {
            setFilterDocNumber(premadeSet.filterDocNum);
        }
        if (premadeSet.filterDocType) {
            setFilterDocType(premadeSet.filterDocType);
        }
        if (premadeSet.filterIO) {
            setFilterDirection(premadeSet.filterIO);
        }
        if (premadeSet.filterPONum) {
            setFilterPO(premadeSet.filterPONum);
        }
        if (premadeSet.filterPartner) {
            setFilterPartnerName(premadeSet.filterPartner);
        }
        if (premadeSet.filterProcess) {
            setFilterProcessName(premadeSet.filterProcess);
        }
        if (premadeSet.filterStatusVal) {
            setFilterStatus(premadeSet.filterStatusVal);
        }
        if (premadeSet.filterFileName) {
            setFilterFileName(premadeSet.filterFileName);
        }
        if (premadeSet.filterCategory) {
            setFilterCategoryId(premadeSet.filterCategory);
        }

        // onSearchClick relies on the all the states to set in time,
        // set them above for filterbar visuals
        // but make the call directly with the premade filters
        onFilterChanged(
            premadeSet.filterClient, 
            undefined, // filterClientId - not available in premade set yet
            premadeSet.filterIO, 
            premadeSet.filterDocType, 
            premadeSet.filterPartner, 
            undefined, // filterPartnerId - not available in premade set yet
            premadeSet.filterPONum, 
            premadeSet.filterDocNum, 
            premadeSet.filterStatusVal, 
            premadeSet.filterProcess, 
            premadeSet.filterDocDateFrom, 
            premadeSet.filterDocDateTo,
            premadeSet.filterFileName,
            premadeSet.filterCategory
        );
    };

    const clearAndRemoveLocalFilters = () => {
        removeLocalStorageItem(viewer, 'errorLogFilterDirection');
        removeLocalStorageItem(viewer, 'errorLogFilterProcessName');
        removeLocalStorageItem(viewer, 'errorLogFilterStatus');
        removeLocalStorageItem(viewer, 'errorLogFilterDateFrom');
        removeLocalStorageItem(viewer, 'errorLogFilterDateTo');
        removeLocalStorageItem(viewer, 'errorLogFilterClientName');
        removeLocalStorageItem(viewer, 'errorLogFilterClientId');
        removeLocalStorageItem(viewer, 'errorLogFilterDocType');
        removeLocalStorageItem(viewer, 'errorLogFilterPONum');
        removeLocalStorageItem(viewer, 'errorLogFilterDocNum');
        removeLocalStorageItem(viewer, 'errorLogFilterPartnerName');
        removeLocalStorageItem(viewer, 'errorLogFilterPartnerId');
        removeLocalStorageItem(viewer, 'errorLogFilterFileName');
        removeLocalStorageItem(viewer, 'errorLogFilterCategory');

        clearFilters();
    };

    const clearFilters = () => {
        clearClientNameFilter();
        clearDocTypeFilter();
        clearPOFilter();
        clearDocNumFilter();
        clearPartnerNameFilter();
        clearFileNameFilter();
        setFilterDirection(undefined);
        setFilterProcessName(undefined);
        setFilterStatus(undefined);
        setFilterDateFrom(undefined);
        setFilterDateTo(undefined);
        setFilterCategoryId(undefined);
        
        setSearchDisabled(true);
    };

    const clearClientNameFilter = () => {
        setFilterClientName(undefined);
        setFilterClientId(undefined);
        setSearchClientName('');
        dispatch(fetchClientNameSearchResultsSuccess());
    };

    const clearDocTypeFilter = () => {
        setFilterDocType(undefined);
    };
  
    const clearPOFilter = () => {
        setFilterPO(undefined);
    };

    const clearDocNumFilter = () => {
        setFilterDocNumber(undefined);
    };

    const clearFileNameFilter = () => {
        setFilterFileName(undefined);
    };

    const clearPartnerNameFilter = () => {
        setFilterPartnerName(undefined);
        setFilterPartnerId(undefined);
        setSearchPartnerName('');
        dispatch(fetchTradingPartnerNameSearchResultsSuccess());
    };

    // Manual clears by adornment needs to adjust the local storage (change event will not be called)
    // Start Adornment manual clear handlers
    const clearClientNameFilterByX = () => {
        clearClientNameFilter();
        removeLocalStorageItem(viewer, 'errorLogFilterClientName');
        removeLocalStorageItem(viewer, 'errorLogFilterClientId');
    };
    const clearDocTypeFilterByX = () => {
        setFilterDocType(undefined);
        removeLocalStorageItem(viewer, 'errorLogFilterDocType');
    };
    const clearPOFilterByX = () => {
        setFilterPO(undefined);
        removeLocalStorageItem(viewer, 'errorLogFilterPONum');
    };
    const clearDocNumFilterByX = () => {
        setFilterDocNumber(undefined);
        removeLocalStorageItem(viewer, 'errorLogFilterDocNum');
    };
    const clearPartnerNameFilterByX = () => {
        clearPartnerNameFilter();    
        removeLocalStorageItem(viewer, 'errorLogFilterPartnerName');
        removeLocalStorageItem(viewer, 'errorLogFilterPartnerId');
    };
    const clearFileNameFilterByX = () => {
        clearFileNameFilter();    
        removeLocalStorageItem(viewer, 'errorLogFilterFileName');
    };
    // End Adornment manual clear handlers        
    
    // Helper function to set client name as manual text entry (shared by onChange and onBlur)
    const setClientNameAsManualEntry = (stringValue: string): void => {
        setFilterClientName(stringValue);
        setFilterClientId(undefined);
        if (stringValue?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterClientName', stringValue);
        } else {
            removeLocalStorageItem(viewer, 'errorLogFilterClientName');
        }
        removeLocalStorageItem(viewer, 'errorLogFilterClientId');
    };

    const onClientNameSearch = (searchValue: string) => {
        // once the delay time has elapsed in the autocomplete component
        // the search will be triggered here to fetch client name search results
        dispatch(fetchClientNameSearchResults(searchValue));
    };

    const onClientNameSearchValueChange = (value: string): void => {
            setSearchClientName(value);
    };

    const onClientNameFilterChange = (value: string | AutocompleteSearchResultModel): void => {
        if (value &&typeof value === 'object' && value.id && value.name) {
            // User selected from dropdown - store both ID and name
            setFilterClientId(value.id);
            setFilterClientName(value.name);
            setLocalStorageItem(viewer, 'errorLogFilterClientName', value.name);
            setLocalStorageItem(viewer, 'errorLogFilterClientId', value.id);
        } else {
            // User typed manually - use name, clear ID
            const stringValue = value as string;
            setClientNameAsManualEntry(stringValue);
        }
    };

    const onClientNameFilterBlur = (): void => {
        // filterClientName will only be populated if a user selects a search result,
        // so if it doesn't have a value or hasn't been updated to the search value, 
        // set it to the searchClientName value if it has one
        if (searchClientName && filterClientName !== searchClientName) {
            setClientNameAsManualEntry(searchClientName);
        }
    };

    const onPOFilterChange = (error: ChangeEvent<HTMLInputElement>) => {
        let value = error.target.value;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterPONum', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterPONum');
        }
        setFilterPO(value);
    };

    const onDocNumFilterChange = (error: ChangeEvent<HTMLInputElement>) => {
        let value = error.target.value;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterDocNum', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterDocNum');
        }
        setFilterDocNumber(value);
    };

    const onFileNameFilterChange = (error: ChangeEvent<HTMLInputElement>) => {
        let value = error.target.value;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterFileName', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterFileName');
        }
        setFilterFileName(value);
    };
    
    // Helper function to set partner name as manual text entry (shared by onChange and onBlur)
    const setPartnerNameAsManualEntry = (stringValue: string): void => {
        setFilterPartnerName(stringValue);
        setFilterPartnerId(undefined);
        if (stringValue?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterPartnerName', stringValue);
        } else {
            removeLocalStorageItem(viewer, 'errorLogFilterPartnerName');
        }
        removeLocalStorageItem(viewer, 'errorLogFilterPartnerId');
    };

    const onPartnerNameSearch = (searchValue: string) => {
        // once the delay time has elapsed in the autocomplete component
        // the search will be triggered here to fetch partner name search results
        dispatch(fetchTradingPartnerNameSearchResults(searchValue));
    };

    const onPartnerNameSearchValueChange = (value: string): void => {
            setSearchPartnerName(value);
    };

    const onPartnerNameFilterChange = (value: string | AutocompleteSearchResultModel): void => {
        if (value && typeof value === 'object' && value.id && value.name) {
            // User selected from dropdown - store both ID and name
            setFilterPartnerId(value.id);
            setFilterPartnerName(value.name);
            setLocalStorageItem(viewer, 'errorLogFilterPartnerName', value.name);
            setLocalStorageItem(viewer, 'errorLogFilterPartnerId', value.id);
        } else {
            // User typed manually - use name, clear ID
            const stringValue = value as string;
            setPartnerNameAsManualEntry(stringValue);
        }
    };

    const onPartnerNameFilterBlur = (): void => {
        // filterPartnerName will only be populated if a user selects a search result,
        // so if it doesn't have a value or hasn't been updated to the search value, 
        // set it to the searchPartnerName value if it has one
        if (searchPartnerName && filterPartnerName !== searchPartnerName) {
            setPartnerNameAsManualEntry(searchPartnerName);
        }
    };

    const onProcessFilterChange = (error: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = error;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterProcessName', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterProcessName');
        }

        let currentValue = value?.length > 0 ? value as ProcessName : undefined;
        
        setFilterProcessName(currentValue);
    };

    const onCategoryFilterChange = (error: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = error;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterCategory', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterCategory');
        }

        let currentValue = value?.length > 0 ? value : undefined;
        
        setFilterCategoryId(currentValue);
    };

    const onStatusFilterChange = (error: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = error;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterStatus', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterStatus');
        }

        let currentValue = value?.length > 0 ? value as ExceptionStatus : undefined;
        setFilterStatus(currentValue);
    };
    
    const onDateFromFilterChange = (value: DateTime | null) => {
        let newDateFrom = value ?? undefined;
        if (newDateFrom) {
            setLocalStorageItem(viewer, 'errorLogFilterDateFrom', newDateFrom.toISO());
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterDateFrom');
        }
        setFilterDateFrom(newDateFrom);

        let newDateTo = newDateFrom?.endOf('month') ?? undefined;
        if (newDateTo) {
            setLocalStorageItem(viewer, 'errorLogFilterDateTo', newDateTo.toISO());
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterDateTo');
        }
        setFilterDateTo(newDateTo);
    };
    
    const onDateToFilterChange = (value: DateTime | null) => {
        if (value) {
            setLocalStorageItem(viewer, 'errorLogFilterDateTo', value.toISO());
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterDateTo');
        }
        setFilterDateTo(value ?? undefined);
    };
    
    const onDirectionFilterChanged = (error: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = error;
        if (value?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterDirection', value);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterDirection');
        }

        let currentValue = value?.length > 0 ? value : undefined;
        setFilterDirection(currentValue);
    };

    const onDocTypeFilterChange = (error: ChangeEvent<HTMLInputElement>) => {
        let typeValue = error.target.value;
        if (typeValue?.length > 0) {
            setLocalStorageItem(viewer, 'errorLogFilterDocType', typeValue);
        }
        else {
            removeLocalStorageItem(viewer, 'errorLogFilterDocType');
        }
        setFilterDocType(typeValue);
    };

   // generate direction filter list 
   const getDirectionFilterList = () => {
       
        let items = [];
        const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
        const inboundItem = <MenuItem key="1" value="I">I</MenuItem>;
        const outboundItem = <MenuItem key="2" value="O">O</MenuItem>;
        items.push(blankItem);
        items.push(inboundItem);
        items.push(outboundItem);
        return items;
    };

    const getProcessFilterList = () => {
        if (processes && processes.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
            const mappedItems = (
                processes.map((process: ProcessName) => (
                    <MenuItem
                        key={process}
                        value={process}
                        disabled={fieldsDisabled}
                    >
                        {capitalizeWithUnderscoreRemoval(process)}
                    </MenuItem>
                ))
            );
            items.push(blankItem);
            items.push(...mappedItems);
            return items;
        }
        return null;
    };

    const getStatusFilterList = () => {
        if (statuses && statuses.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
            const errorStatuses = [ExceptionStatus.Error, ExceptionStatus.ResolvedError];
            const mappedItems = (
                errorStatuses.map((status: ExceptionStatus) => (
                    <MenuItem
                        key={status}
                        value={status}
                        disabled={fieldsDisabled}
                    >
                        {capitalizeWithUnderscoreRemoval(status, ' ')}
                    </MenuItem>
                ))
            );
            items.push(blankItem);
            items.push(...mappedItems);
            return items;
        }
        return null;
    };

    const getCategoryFilterList = () => {
        if (categories && categories.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
            const mappedItems = (
                categories.map((category: TransactionErrorCategoryModel) => (
                    <MenuItem
                        key={category.id}
                        value={category.id}
                        disabled={fieldsDisabled}
                    >
                        {category.name}
                    </MenuItem>
                ))
            );
            items.push(blankItem);
            items.push(...mappedItems);
            return items;
        }
        return null;
    };

    const docTypeFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear document type filter"
                    onClick={clearDocTypeFilterByX}
                    disabled={fieldsDisabled}
                >
                    {filterDocType && filterDocType.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };

    const pOFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear po filter"
                    onClick={clearPOFilterByX}
                    disabled={fieldsDisabled}
                >
                    {filterPO && filterPO.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };

    const docNumFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear document number filter"
                    onClick={clearDocNumFilterByX}
                    disabled={fieldsDisabled}
                >
                    {filterDocNumber && filterDocNumber.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };

    const fileNameFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear file name filter"
                    onClick={clearFileNameFilterByX}
                    disabled={fieldsDisabled}
                >
                    {filterFileName && filterFileName.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };
    
    return (
        <FilterBar 
            id={id} 
            visible={visible} 
            onClose={onCloseClick} 
            onSearch={onSearchClick} 
            barHeight="140px"
            searchButtonDisabled={searchDisabled}
            searchButtonTitle={searchDisabled ? "Must have a filter to Search" : undefined}
        >
            <Stack direction={'column'} justifyContent="center" rowGap={2} width="100%" >
                <Grid container item direction={'row'} ml={{xs:1, sm:3, md:6, lg:10, xl:14}} columnGap={2} justifyContent="flex-start" xs={12}>
                    <Grid item xs={2}>
                        <AutocompleteFilterField
                            fieldId="error-log-filter-client-name"
                            searchTerm={searchClientName}
                            onSearchTermValueChange={onClientNameSearchValueChange}
                            filterValue={filterClientName ?? ''}
                            onFilterValueChange={onClientNameFilterChange}
                            clearIndicatorOnClick={clearClientNameFilterByX}
                            searchResults={clientNameSearchResults}
                            autoFocus={true}
                            label="Client Name"
                            disabled={fieldsDisabled}
                            onFieldBlur={onClientNameFilterBlur}
                            search={onClientNameSearch}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            itemID="error-log-filter-po"
                            fullWidth
                            value={filterPO ?? ''}
                            label="PO Number"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'po number', 'maxLength': 50, }}
                            InputProps={pOFilterProps}
                            onChange={onPOFilterChange}
                            autoComplete="off"
                            data-cy="error-log-filter-po"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControl variant="standard" fullWidth disabled={fieldsDisabled}>
                            <InputLabel id="error-log-filter-process-label">Process Name</InputLabel>
                            <Select
                                labelId="error-log-filter-process-label"
                                aria-labelledby="error-log-filter-process-label"
                                id="error-log-filter-process"
                                value={filterProcessName ?? ''}
                                MenuProps={{
                                    'aria-label': 'edi process name',
                                }}
                                onChange={onProcessFilterChange}
                                data-cy="error-log-filter-process"
                            >
                                {getProcessFilterList()}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DatePickerWrapper>
                                <DateTimePicker
                                    label="S/R Date From"
                                    value={filterDateFrom ?? null}
                                    views={['year','month','day','hours','minutes']}
                                    openTo={'month'}
                                    disabled={fieldsDisabled}
                                    onChange={onDateFromFilterChange}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                            inputProps: {'aria-label': 'date from'}
                                        },
                                        field: { clearable: true }
                                    }}
                                    data-cy="error-log-filter-date-from"
                                ></DateTimePicker>
                            </DatePickerWrapper>
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={2}>
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DatePickerWrapper>
                                <DateTimePicker
                                    label="S/R Date To"
                                    value={filterDateTo ?? null}
                                    views={['year','month','day','hours','minutes']}
                                    openTo={'day'}
                                    minDateTime={filterDateFrom}
                                    disabled={fieldsDisabled}
                                    onChange={onDateToFilterChange}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                            inputProps: {'aria-label': 'date to'}
                                        },
                                        field: { clearable: true }
                                    }}
                                    data-cy="error-log-filter-date-to"
                                ></DateTimePicker>
                            </DatePickerWrapper>
                        </LocalizationProvider>
                    </Grid>
                </Grid>
                <Grid container item direction={'row'} ml={{xs:1, sm:3, md:6, lg:10, xl:14}} columnGap={2} xs={12} justifyContent="flex-start">
                    <Grid item xs={2}>
                        <AutocompleteFilterField
                            fieldId="error-log-filter-partner-name"
                            searchTerm={searchPartnerName}
                            onSearchTermValueChange={onPartnerNameSearchValueChange}
                            filterValue={filterPartnerName ?? ''}
                            onFilterValueChange={onPartnerNameFilterChange}
                            clearIndicatorOnClick={clearPartnerNameFilterByX}
                            searchResults={partnerNameSearchResults}
                            label="Trading Partner Name"
                            disabled={fieldsDisabled}
                            onFieldBlur={onPartnerNameFilterBlur}
                            search={onPartnerNameSearch}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            itemID="error-log-filter-doc-num"
                            fullWidth
                            value={filterDocNumber ?? ''}
                            label="Document Number"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'document number', 'maxLength': 50, }}
                            InputProps={docNumFilterProps}
                            onChange={onDocNumFilterChange}
                            autoComplete="off"
                            data-cy="error-log-filter-doc-num"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormControl variant="standard" fullWidth disabled={fieldsDisabled}>
                            <InputLabel id="error-log-filter-category-label">Category</InputLabel>
                            <Select
                                labelId="error-log-filter-category-label"
                                aria-labelledby="error-log-filter-category-label"
                                id="error-log-filter-category"
                                value={filterCategoryId ?? ''}
                                MenuProps={{
                                    'aria-label': 'error category',
                                }}
                                onChange={onCategoryFilterChange}
                                data-cy="error-log-filter-category"
                            >
                                {getCategoryFilterList()}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            itemID="error-log-filter-doc-type"
                            fullWidth
                            value={filterDocType ?? ''}
                            label="Type"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'document type', 'maxLength': 50, }}
                            InputProps={docTypeFilterProps}
                            onChange={onDocTypeFilterChange}
                            autoComplete="off"
                            data-cy="error-log-filter-doc-type"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <TextField
                            itemID="error-log-filter-file-name"
                            fullWidth
                            value={filterFileName ?? ''}
                            label="File Name"
                            disabled={fieldsDisabled}
                            inputProps={{ 'aria-label': 'file name', 'maxLength': 50, }}
                            InputProps={fileNameFilterProps}
                            onChange={onFileNameFilterChange}
                            autoComplete="off"
                            data-cy="error-log-filter-file-name"
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <FormControl variant="standard" fullWidth disabled={fieldsDisabled}>
                            <InputLabel id="error-log-filter-status-label">Status</InputLabel>
                            <Select
                                labelId="error-log-filter-status-label"
                                aria-labelledby="error-log-filter-status-label"
                                id="error-log-filter-status"
                                value={filterStatus ?? ''}
                                MenuProps={{
                                    'aria-label': 'status',
                                }}
                                onChange={onStatusFilterChange}
                                data-cy="error-log-filter-status"
                            >
                                {getStatusFilterList()}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={1}>
                        <FormControl variant="standard" fullWidth disabled={fieldsDisabled}>
                            <InputLabel id="error-log-filter-direction-label">I/O</InputLabel>
                            <Select
                                labelId="error-log-filter-direction-label"
                                aria-labelledby="error-log-filter-direction-label"
                                id="error-log-filter-direction"
                                value={filterDirection ?? ''}
                                MenuProps={{
                                    'aria-label': 'transaction direction',
                                }}
                                onChange={onDirectionFilterChanged}
                                data-cy="error-log-filter-direction"
                            >
                                {getDirectionFilterList()}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Stack>
        </FilterBar>
    );
};

export default ErrorLogsFilterBar;
